import { useEffect, useState } from "react";
import { Download, Search, Loader2, Eye } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { STATUS_STYLE, STATUS_LABEL, inr } from "@/lib/branch";
import { useAuth } from "@/lib/auth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { AdminBranchDialog } from "@/components/AdminBranchDialog";
import { AdminBranchSettings } from "@/components/AdminBranchSettings";

const SUMMARY = [["total_users", "Registered Users"], ["total_branches", "Created Branches"], ["active_branches", "Active Branches"],
  ["pending_branches", "Pending Branches"], ["suspended_branches", "Suspended Branches"], ["genuine_customers", "Paying Customers"],
  ["verified_orders", "Verified Orders"], ["product_sales", "Product Sales", 1], ["commission_liability", "Commission Liability", 1],
  ["commission_paid_out", "Paid-out (withdrawals)", 1], ["refunds_amount", "Refunds", 1]];

export default function AdminBranches() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState(null);
  const [filters, setFilters] = useState({ q: "", status: "", date_from: "", date_to: "" });
  const [applied, setApplied] = useState(filters);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(null);

  const load = () => {
    api.get("/admin/branches/summary").then(({ data }) => setSummary(data));
    setRows(null);
    api.get("/admin/branches", { params: { ...applied, page } }).then(({ data }) => setRows(data)).catch(() => toast.error("Could not load branches"));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [applied, page]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/branches/export", { params: applied, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data); a.download = "branches.csv"; a.click();
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-branches-page">
      <div className="flex flex-wrap justify-between items-end gap-3">
        <div><h1 className="font-display text-3xl font-bold">Multi-Branch Management</h1><p className="text-sm text-[#B91C1C]">Every branch across all accounts, with sponsor inheritance and activation orders.</p></div>
        {isSuper && <button onClick={exportCsv} className="btn-outline text-sm" data-testid="admin-branches-export-btn"><Download className="w-4 h-4" /> Export CSV</button>}
      </div>
      <Tabs defaultValue="branches">
        <TabsList><TabsTrigger value="branches" data-testid="admin-branches-tab">Branches</TabsTrigger><TabsTrigger value="settings" data-testid="admin-branch-settings-tab">Settings</TabsTrigger></TabsList>
        <TabsContent value="branches" className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {SUMMARY.map(([k, l, m]) => <div key={k} className="bg-white rounded-2xl border border-[#FECACA] p-3"><div className="text-[10px] uppercase text-[#B91C1C]">{l}</div>
              {summary ? <div className="font-numbers font-bold text-lg" data-testid={`admin-branch-summary-${k}`}>{m ? inr(summary[k]) : summary[k]}</div> : <Skeleton className="h-6 w-16" />}</div>)}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); setPage(1); setApplied(filters); }} className="flex flex-wrap gap-2">
            <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[#94A3B8]" />
              <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Branch, referral code, owner, mobile" className="input-soft pl-8 py-2 text-sm w-64" data-testid="admin-branches-search" /></div>
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="input-soft py-2 text-sm w-40" data-testid="admin-branches-status-filter">
              <option value="">All statuses</option>{Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="input-soft py-2 text-sm w-40" data-testid="admin-branches-from" />
            <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="input-soft py-2 text-sm w-40" data-testid="admin-branches-to" />
            <button className="btn-primary text-sm" data-testid="admin-branches-apply">Apply</button>
          </form>
          <div className="bg-white rounded-2xl border border-[#FECACA] overflow-x-auto">
            {!rows ? <div className="p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div> : (
              <table className="w-full text-xs" data-testid="admin-branches-table">
                <thead className="bg-[#FEF2F2] uppercase"><tr>{["Branch", "Owner", "Original sponsor", "Sponsor branch", "Referral", "Created", "Activated", "Order", "Status", "Direct", "Downline", "Sales", "Commission", ""].map((h) => <th key={h} className="text-left px-3 py-2 whitespace-nowrap">{h}</th>)}</tr></thead>
                <tbody>
                  {rows.branches.length === 0 && <tr><td colSpan={14} className="text-center py-10 text-[#94A3B8]">No branches match.</td></tr>}
                  {rows.branches.map((b) => (
                    <tr key={b.id} className="border-t border-[#FEE2E2]" data-testid={`admin-branch-row-${b.referral_code}`}>
                      <td className="px-3 py-2 whitespace-nowrap"><b>{b.branch_code}</b><div className="text-[#94A3B8]">{b.name}</div></td>
                      <td className="px-3 py-2 whitespace-nowrap">{b.owner_name}<div className="text-[#94A3B8]">{b.owner_code}</div></td>
                      <td className="px-3 py-2 whitespace-nowrap">{b.sponsor_name || "—"}<div className="text-[#94A3B8]">{b.sponsor_code}</div></td>
                      <td className="px-3 py-2 font-mono">{b.original_sponsor_branch_code || "—"}</td>
                      <td className="px-3 py-2 font-mono">{b.referral_code}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{b.created_at ? new Date(b.created_at).toLocaleDateString("en-IN") : "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{b.activated_at ? new Date(b.activated_at).toLocaleDateString("en-IN") : "—"}</td>
                      <td className="px-3 py-2 font-mono">{b.activation_order_id ? b.activation_order_id.slice(0, 8) : "—"}</td>
                      <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded border ${STATUS_STYLE[b.status]}`}>{STATUS_LABEL[b.status]}</span></td>
                      <td className="px-3 py-2 font-numbers">{b.direct_referrals}</td><td className="px-3 py-2 font-numbers">{b.downline_members}</td>
                      <td className="px-3 py-2 font-numbers">{inr(b.eligible_sales)}</td><td className="px-3 py-2 font-numbers">{inr(b.earned_commission)}</td>
                      <td className="px-3 py-2"><button onClick={() => setOpen(b.id)} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#FECACA] hover:bg-[#FEE2E2]" data-testid={`admin-branch-view-${b.referral_code}`}><Eye className="w-3 h-3" /> View</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {rows && rows.pages > 1 && (
              <div className="flex items-center gap-2 text-xs p-3">
                <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-2 py-1 border rounded disabled:opacity-40" data-testid="admin-branches-prev">Prev</button>
                <span>Page {page} / {rows.pages} · {rows.total} branches</span>
                <button disabled={page >= rows.pages} onClick={() => setPage(page + 1)} className="px-2 py-1 border rounded disabled:opacity-40" data-testid="admin-branches-next">Next</button>
              </div>
            )}
          </div>
        </TabsContent>
        <TabsContent value="settings"><AdminBranchSettings canEdit={isSuper} /></TabsContent>
      </Tabs>
      <AdminBranchDialog id={open} onClose={() => setOpen(null)} onChanged={load} canAct={isSuper} />
    </div>
  );
}

